import { sdkRequest } from '@eventbrite/http';
import { formatUrl } from 'url-lib';
import { searchAllEvents } from '../../../../api/searchAllEvents';
import { searchOrganicEvents } from '../../../../api/searchOrganicEvents';
import { FEATURE_EVENTS_MAPPING } from '../../../../constants/configs/featurePaidEventsMapping';
import { IBrowseFilters } from '../../../../constants/filters';
import { bboxShape } from '../../../../types/index';
import { bboxToUrlParam } from '../../../../utils/location';

const EVENT_EXPANSIONS = [
    'primary_venue',
    'image',
    'ticket_availability',
    'saves',
    'event_sales_status',
    'primary_organizer',
    'public_collections',
];

export const getUserSavedEvents = () => {
    const url = formatUrl('/api/v3/destination/saved/', {
        'expand.destination_event': EVENT_EXPANSIONS,
        page_size: 4,
    });

    return sdkRequest(url);
};

/**
 * This method returns events from organizers the user is following.
 */
export const getFollowedProfiles = () => {
    const maxEvents = 16;

    return searchOrganicEvents({
        event_search: {
            dates: ['current_future'],
            page: 1,
            image: true,
            special: ['organizers_i_follow'],
            sort: 'date',
            page_size: maxEvents,
        },
        'expand.destination_event': EVENT_EXPANSIONS,
        browse_surface: 'homepage',
    });
};

// This endpoint returns organizers the users follows
export const getUserFollowing = (userId: string) => {
    const url = formatUrl(`/api/v3/destination/users/${userId}/following/`);

    return sdkRequest(url);
};

/*
 * Users can optionally apply filters (ie. Date) to personalized content.
 * buildPayload parses the filters into either data (filterData) or
 * payload params (filterParams) for the recommendations API endpoint
 */

const buildPayload = (filters?: IBrowseFilters[]) => {
    const payload = filters?.reduce(
        (prev, curr) => {
            let filterParams = {};
            let filterData = {};

            if (curr.payload === 'param') {
                filterParams = {
                    ...prev.filterParams,
                    [curr.name as string]: curr.value,
                };
            } else if (curr.payload === 'data') {
                filterData = {
                    ...prev.filterData,
                    [curr.name as string]: curr.value,
                };
            }
            return {
                filterParams,
                filterData,
            };
        },
        {
            filterParams: {},
            filterData: {},
        },
    );

    return payload;
};

// This endpoint returns events and organizers personalized for the user
export const getUserPersonalizedContent = async (
    locale?: string | null,
    filters?: Array<object>,
) => {
    const EVENT_EXPANSIONS =
        'event_sales_status,image,primary_venue,saves,series,ticket_availability,primary_organizer';
    const PROFILE_EXPANSIONS = 'image';

    const params = {
        'expand.destination_event': EVENT_EXPANSIONS,
        'expand.destination_profile': PROFILE_EXPANSIONS,
        locale: locale,
    };

    const payload = buildPayload(filters);
    let postData = {};

    const url = formatUrl(
        '/api/v3/destination/users/me/recommendations/my_feed_recommendations_all/',
        {
            ...params,
            ...payload?.filterParams,
        },
    );

    if (payload?.filterData && JSON.stringify(payload?.filterData) !== '{}') {
        postData = {
            method: 'POST',
            body: JSON.stringify(payload.filterData),
        };
    }

    const personalizedContent = await sdkRequest(url, postData);

    return personalizedContent;
};

// This endpoint returns personalized event recommendations
export const getUserEventRecommendations = async () => {
    const recommendedEventIds = await sdkRequest(
        '/api/v3/destination/users/me/recommendations/my_offline_events_recommendation/',
    );
    let recommendedEvents = [];

    if (recommendedEventIds.my_offline_events_recommendation.length) {
        const inflateUrl = formatUrl(`/api/v3/destination/events/`, {
            'expand.destination_event': EVENT_EXPANSIONS,
            event_ids:
                recommendedEventIds.my_offline_events_recommendation.join(','),
        });
        recommendedEvents = await sdkRequest(inflateUrl);
    }

    return recommendedEvents;
};

export const fetchCityBrowseEvents = (placeId: string) => {
    const url = formatUrl(`/api/v3/destination/city-browse/${placeId}/`, {
        'expand.destination_event': EVENT_EXPANSIONS,
        buckets_list: 'best_of_city_events',
    });

    return sdkRequest(url);
};

export const searchEventsBy = (placeId: string) => {
    return searchOrganicEvents({
        event_search: {
            places: [placeId],
            dates: ['current_future'],
            page_size: 30,
            image: true,
        },
        'expand.destination_event': EVENT_EXPANSIONS,
        browse_surface: 'homepage',
    });
};

// get paid events for geo location
export const searchFeaturePaidEvents = (placeId: string) => {
    const event_ids =
        placeId in FEATURE_EVENTS_MAPPING
            ? FEATURE_EVENTS_MAPPING[placeId]
            : [];
    if (event_ids.length == 0) {
        return Promise.resolve(undefined);
    }
    const url = formatUrl(`/api/v3/destination/events/`, {
        'expand.destination_event': EVENT_EXPANSIONS,
        event_ids: event_ids.join(','),
    });
    return sdkRequest(url);
};

export const searchEventsWithParameters = ({
    placeId,
    bbox,
    eventSearch,
    isOnline,
}: {
    placeId: string;
    bbox: bboxShape;
    eventSearch: {
        dates: string[];
        tags: string;
    };
    isOnline: boolean;
}) => {
    let searchOptions: any = {
        places: [placeId],
    };

    // sometimes google maps autocomplete returns with a lat lng that we can't
    // translate to a place id. In that case, we resort to sending a bounding box.
    if (!placeId && bbox) {
        searchOptions = {
            bbox: bboxToUrlParam(bbox),
        };
    }

    // Let's default to an online search if place or bbox still are not present.
    if (isOnline || (!placeId && !bbox)) {
        searchOptions = {
            online_events_only: true,
        };
    }
    return searchAllEvents({
        interfaceName: 'homepage',
        slots: 1,
        event_search: {
            page_size: 30,
            image: true,
            ...searchOptions,
            ...eventSearch,
        },
        'expand.destination_event': EVENT_EXPANSIONS,
        browse_surface: 'homepage',
    });
};
